import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Mail, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { createContact, getConfig, waLink } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { CONTACT } from "@/constants/testIds";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState({ company_email: "", company_whatsapp: "" });

  useEffect(() => {
    getConfig().then(setConfig).catch(() => {});
  }, []);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.message.trim()) {
      toast.error("Compila almeno nome e messaggio.");
      return;
    }
    setLoading(true);
    // Salviamo la richiesta (best-effort) e apriamo WhatsApp con il messaggio precompilato
    createContact(form).catch(() => {});
    const lines = [
      `Ciao! Sono ${form.name}.`,
      form.email ? `Email: ${form.email}` : null,
      form.phone ? `Telefono: ${form.phone}` : null,
      "",
      `Messaggio: ${form.message}`,
    ].filter((l) => l !== null);
    const text = lines.join("\n");
    const number = config.company_whatsapp || "+39 348 726 0162";
    const href = waLink(number, text);
    window.open(href, "_blank", "noopener,noreferrer");
    toast.success("Ti reindirizzo su WhatsApp...");
    setForm({ name: "", email: "", phone: "", message: "" });
    setLoading(false);
  };

  return (
    <section id="contatti" data-testid={CONTACT.section} className="py-16 sm:py-24">
      <div className="container-x grid lg:grid-cols-2 gap-10">
        <Reveal>
          <p className="text-sm font-semibold text-accent uppercase tracking-wider">Contatti</p>
          <h2 className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tight">Hai bisogno di aiuto?</h2>
          <p className="mt-4 text-muted-foreground text-base leading-relaxed">
            Compila il modulo qui sotto: ti risponderemo <strong className="text-foreground">direttamente su WhatsApp</strong>. Il nostro team è a tua disposizione per abbonamenti, telefoni e installazione.
          </p>
          <div className="mt-8 space-y-4">
            {config.company_email && (
              <div className="flex items-center gap-3 rounded-2xl border border-border bg-card p-4 glow">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-accent"><Mail className="h-5 w-5" /></span>
                <div>
                  <p className="text-xs text-muted-foreground">Email</p>
                  <a href={`mailto:${config.company_email}`} className="text-sm font-medium hover:text-accent break-all">{config.company_email}</a>
                </div>
              </div>
            )}
            {config.company_whatsapp && (
              <a href={waLink(config.company_whatsapp, "Ciao! Vorrei informazioni su controllatelefoni.it")} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 rounded-2xl border border-border bg-card p-4 glow glow-hover transition-colors hover:border-accent">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-accent"><MessageCircle className="h-5 w-5" /></span>
                <div>
                  <p className="text-xs text-muted-foreground">WhatsApp</p>
                  <p className="text-sm font-medium">{config.company_whatsapp}</p>
                </div>
              </a>
            )}
          </div>
        </Reveal>

        <Reveal delay={0.08}>
          <Card className="p-6 sm:p-8 rounded-2xl border-border bg-card glow">
            <form onSubmit={submit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="c-name">Nome e cognome</Label>
                  <Input id="c-name" data-testid={CONTACT.name} value={form.name} onChange={update("name")} placeholder="Mario Rossi" className="rounded-xl" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="c-phone">Telefono (opzionale)</Label>
                  <Input id="c-phone" data-testid={CONTACT.phone} value={form.phone} onChange={update("phone")} placeholder="+39 333 1234567" className="rounded-xl" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="c-email">Email</Label>
                <Input id="c-email" type="email" data-testid={CONTACT.email} value={form.email} onChange={update("email")} placeholder="mario@email.it" className="rounded-xl" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="c-message">Messaggio</Label>
                <Textarea id="c-message" data-testid={CONTACT.message} value={form.message} onChange={update("message")} placeholder="Scrivi qui la tua richiesta..." rows={5} className="rounded-xl" />
              </div>
              <Button type="submit" disabled={loading} data-testid={CONTACT.submit} className="w-full rounded-xl glow-hover h-11 text-white" style={{ backgroundColor: "#25D366" }}>
                {loading ? "Apertura..." : (<><MessageCircle className="h-4 w-4" /> Invia su WhatsApp</>)}
              </Button>
              <p className="text-center text-xs text-muted-foreground">Si aprirà una chat WhatsApp con il messaggio già precompilato.</p>
            </form>
          </Card>
        </Reveal>
      </div>
    </section>
  );
};
